import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from "react";
import { dictionaries, type Locale, type Strings } from "./strings";
import { withOverrides } from "./overrides";
import { useMarket } from "@/v3/MarketProvider";

/* Locale runtime.
 *
 * Arabic is the default locale, so <html lang="ar" dir="rtl"> is the initial
 * state on a first visit -- matching /legacy, whose own default was
 * `loadSharedLang() || prefs.lang || "ar"`. English is reached only through
 * the header toggle.
 *
 * There is no theme state here. /legacy has no dark design, and inventing one
 * would be reintroducing the withdrawn design direction.
 */

const LOCALE_KEY = "souqakar_locale";

type LocaleContextValue = {
  locale: Locale;
  t: Strings;
  dir: "rtl" | "ltr";
  ar: boolean;
  setLocale: (next: Locale) => void;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readStoredLocale(): Locale {
  try {
    const raw = window.localStorage.getItem(LOCALE_KEY);
    if (raw === "ar" || raw === "en") return raw;
  } catch {
    /* storage disabled — fall through to the Arabic default */
  }
  return "ar";
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);
  /* Bassem's copy ruling: `site_texts` rows override the dictionary, per
     market and per locale. The i18n file stays the source of DEFAULTS, so an
     empty override map is the designed wording -- which is what the site
     renders until he edits something, and again if he clears a row. */
  const { texts } = useMarket();

  const t = useMemo(
    () => withOverrides(dictionaries[locale], "app", texts[locale]),
    [locale, texts],
  );
  const dir = t.meta.dir;

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("lang", locale);
    root.setAttribute("dir", dir);
    // The title is NOT set here: each route owns it via useDocumentMeta, so
    // there is only ever one writer.
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", t.meta.documentDescription);
    try { window.localStorage.setItem(LOCALE_KEY, locale); } catch { /* ignore */ }
  }, [locale, dir, t]);

  const setLocale = useCallback((next: Locale) => setLocaleState(next), []);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, t, dir, ar: locale === "ar", setLocale }),
    [locale, t, dir, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used inside <LocaleProvider>");
  return ctx;
}
